import * as React from 'react';
import { useState, useEffect } from 'react';
import { NavLink } from "react-router-dom";
import { 
  Box, Grid, Typography, Card, CardContent, Paper, 
  Stack, Link, Divider 
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import connectionLink from '../connection/Connection';

import zockerImg from '../external/zocker.png';
import modDBImg from '../external/moddb.png';

// Quick Stat Card Component
const StatCard = ({ title, value }: { title: string; value: string | number }) => (
  <Card sx={{ textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center' }}>
    <CardContent sx={{ width: '100%' }}>
      <Typography color="text.secondary" variant="body2" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

export default function HomePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Define columns in middle row
  const columns: GridColDef[] = [
    { 
      field: 'country', // matches "country" in JSON
      headerName: 'Country', 
      flex: 1 
    },
    { 
      field: 'count',   // matches "count" in JSON
      headerName: 'Count', 
      flex: 0.4, 
      align: 'center', 
      headerAlign: 'center',
      // Optional: display "0" as "--" if you want to match your mockup's style
      renderCell: (params) => (params.value === 0 ? '--' : params.value)
    },
  ];

  // Middle row, get stadia per country.
  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${connectionLink}/stadiums/counts-by-country`);
        const data = await response.json();
        setRows(data);
      } catch (err) {
        console.error("Failed to fetch country stats", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  const [totalStadiums, setTotalStadiums] = useState<number | string>('--');
  const [nationalStadiums, setNationalStadiums] = useState<number | string>('--');
  const [clubStadiums, setClubStadiums] = useState<number | string>('--');

  useEffect(() => {
    const fetchTotals = async () => {
      try {
        // Fetch Total Count
        const resTotal = await fetch(`${connectionLink}/stadiums/count/`);
        const dataTotal = await resTotal.json();
        setTotalStadiums(dataTotal.total);

        // Fetch National Teams Count
        const resNational = await fetch(`${connectionLink}/stadiums/search-count?country=National%20Teams`);
        const dataNational = await resNational.json();
        setNationalStadiums(dataNational.total);

        // Calculate Club Stadiums (Total - National)
        // If your API doesn't have a specific endpoint for "Club", 
        // math is the quickest way:
        if (dataTotal.total && dataNational.total) {
          setClubStadiums(dataTotal.total - dataNational.total);
        }
      } catch (err) {
        console.error("Error fetching totals:", err);
      }
    };

    fetchTotals();
  }, []);
  return (
    
    <Box sx={{ flexGrow: 1, p: 3 }}>      
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
      Welcome to Stadium Wizard.
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
      This website will help you easily navigate through available teams, competitions and stadiums in FIFA Manager 23.
        <br />
        To see whether your favorite team has a stadium, go to <Link component={NavLink} to={'teamList'}>Team List</Link> page.
        <br />
        To see what stadiums are available in our collection, go to <Link component={NavLink} to={'stadiumList'}>Stadium List</Link> page.
      </Typography>

      <Grid container spacing={4}>
        {/* LEFT COLUMN: Statistics */}
        <Grid item xs={12} md={3}>
          <Stack spacing={2}>
            <StatCard title="Total number of stadiums available" value={totalStadiums} />
            <StatCard title="Total number of club stadiums available" value={clubStadiums} />
            <StatCard title="Total number of national stadiums available" value={nationalStadiums} />
          </Stack>
        </Grid>

        {/* MIDDLE COLUMN: Country DataGrid */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              // This tells MUI to use the 'country' string as the unique ID
              getRowId={(row) => row.country} 
              density="compact"
              autoHeight
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              pageSizeOptions={[10, 20, 50]}
              // This ensures the search bar works with your custom ID
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                },
              }}
            />
          </Paper>
        </Grid>

        {/* RIGHT COLUMN: Navigation/Images */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Link component={NavLink} to="https://www.fm-zocker.net/" sx={{ textDecoration: 'none' }}>
              <Paper 
                sx={{ 
                  height: 180, 
                  display: 'flex', 
                  flexDirection: 'column', // Stack vertically
                  alignItems: 'center', 
                  justifyContent: 'center',
                  overflow: 'hidden',
                  '&:hover': { border: '2px solid #1976d2' }
                }}
              >
                <Box 
                  component="img"
                  src={zockerImg}
                  sx={{ height: 100, width: 'auto', objectFit: 'contain', mb: 1 }}
                />
                <Typography variant="h6">Visit FM-Zocker.de</Typography>
              </Paper>
            </Link>
             <Link component={NavLink} to="https://www.moddb.com/mods/fifa-manager-season-patch" sx={{ textDecoration: 'none' }}>
              <Paper 
                sx={{ 
                  height: 180, 
                  display: 'flex', 
                  flexDirection: 'column', // Stack vertically
                  alignItems: 'center', 
                  justifyContent: 'center',
                  overflow: 'hidden',
                  '&:hover': { border: '2px solid #1976d2' }
                }}
              >
                <Box 
                  component="img"
                  src={modDBImg}
                  sx={{ height: 100, width: 'auto', objectFit: 'contain', mb: 1 }}
                />
                <Typography variant="h6">Visit our ModDB page</Typography>
              </Paper>
            </Link>
           </Stack>
        </Grid>      </Grid>
    </Box>
  );
}